import { useState } from "react";

import { Popover } from "./ui/Popover";
import { cn } from "./ui/cn";
import type { IntentKind } from "../lib/quoting";
import type { Message } from "../types";

/**
 * The ⋯ menu on a message.
 *
 * Per message rather than per ticket, because in a thread that has been round
 * four people the useful answer to "reply to what" is rarely "the last thing
 * anybody said". Reply all in particular is meaningless at the ticket level:
 * the set of people to answer belongs to one message, and it changes down the
 * thread as the customer adds their colleague and drops their supplier.
 *
 * Notes get only the link. There is nobody to reply to -- a note was never
 * sent to anyone -- and offering Forward on one is offering to email a customer
 * something written specifically so they would not see it.
 */
export function MessageActions({
  message,
  onAct,
  onCopyLink,
}: {
  message: Message;
  onAct: (kind: IntentKind) => void;
  /** Puts a link to this message on the clipboard; true if that worked. */
  onCopyLink: () => Promise<boolean>;
}) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState<"no" | "yes" | "failed">("no");

  const isNote = message.kind === "note";
  const canReply = !isNote && Boolean(message.fromEmail);

  const choose = (kind: IntentKind): void => {
    setOpen(false);
    onAct(kind);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) setCopied("no");
      }}
      label={`Actions for this message`}
      /* Fixed positioning, because the thread scrolls: an absolutely
         positioned panel is clipped by the scroller and the menu would lose
         its bottom two items near the end of a conversation. */
      overlay
      side="bottom"
      align="right"
      className="w-56 p-1"
      trigger={
        <span
          aria-hidden
          /* A finger's width on a phone, where this is the only way to
             Reply all or Forward; the glyph stays small, the target does not. */
          className="flex size-6 items-center justify-center rounded text-muted hover:bg-surface-2 hover:text-brand-600 max-md:size-10 max-md:text-lg dark:hover:text-brand-400"
        >
          ⋯
        </span>
      }
    >
      <ul>
        {canReply ? (
          <>
            <li>
              <Item onClick={() => choose("reply")}>
                Reply
                <Hint>to {message.fromName ?? message.fromEmail}</Hint>
              </Item>
            </li>
            <li>
              <Item onClick={() => choose("reply-all")}>
                Reply to all
                <Hint>everyone on this message</Hint>
              </Item>
            </li>
            <li>
              <Item onClick={() => choose("forward")}>
                Forward
                <Hint>to someone not on the thread</Hint>
              </Item>
            </li>
            <li aria-hidden className="my-1 border-t border-line" />
          </>
        ) : null}

        <li>
          <Item
            onClick={async () => {
              const ok = await onCopyLink();
              setCopied(ok ? "yes" : "failed");
              /* The menu stays open on purpose. This is the one item whose
                 result is invisible -- nothing on screen changes when an
                 address lands on the clipboard -- so it has to be able to say
                 so before it disappears. */
            }}
          >
            {copied === "yes"
              ? "Link copied"
              : copied === "failed"
                ? "Could not copy — select the address by hand"
                : "Copy link to this message"}
            {copied === "no" ? (
              <Hint>opens the ticket at this point in the thread</Hint>
            ) : null}
          </Item>
        </li>
      </ul>
    </Popover>
  );
}

function Item({
  onClick,
  children,
}: {
  onClick: () => void | Promise<void>;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={() => void onClick()}
      className={cn(
        "w-full rounded px-2 py-1.5 text-left text-xs text-content max-md:py-3 max-md:text-sm",
        "hover:bg-surface-2 focus:outline-none focus-visible:bg-surface-2",
      )}
    >
      {children}
    </button>
  );
}

/** The second line, saying what the action will actually do. */
function Hint({ children }: { children: React.ReactNode }) {
  return <span className="block truncate text-[11px] text-faint">{children}</span>;
}
